import { CommonModule } from '@angular/common';
import { Component, EventEmitter, Input, OnChanges, Output, SimpleChanges } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import { MatDialog } from '@angular/material/dialog';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { Observable } from 'rxjs';
import {
  ConversationViewDto,
  DecideDraftBody,
  DriveMode,
  FREEZE_LABELS,
  MODE_LABELS,
  ModeChangeDto,
  OutreachDraftDto,
  OutreachTurnDto,
  ProposalDto,
  STATE_LABELS,
  TurnOutcomeDto,
  outreachError,
} from '../../../data-transfer-objects/outreach.dto';
import { OutreachService } from '../../../services/outreach.service';
import { ProfileDialogService } from '../../../services/profile-dialog.service';
import { PersonLinkComponent } from '../../../components/shared/person-link.component';
import {
  ActionConfirmData,
  ActionConfirmDialogComponent,
  ActionConfirmResult,
} from '../../abuse/action-confirm-dialog/action-confirm-dialog.component';
import { ProposalCardComponent } from '../proposal-card/proposal-card.component';

/** One line of the thread: a message that went out or came in, or a draft that never did. */
export type ThreadItem =
  | { kind: 'turn'; at: string; turn: OutreachTurnDto; draft: OutreachDraftDto | null; draftId: number | null }
  | { kind: 'draft'; at: string; draft: OutreachDraftDto; draftId: number };

/**
 * One Sharon conversation, whole: the thread, the drive mode, the draft
 * waiting for a decision, what Sharon learned from the last edit, and the
 * member and cause beside it.
 *
 * Used by the conversation page (where the alert emails land) and by the
 * simulator, so a draft on a made-up member is decided exactly the way a real
 * one is. Give it an id and it loads; give it a view (the simulator already
 * holds one from its last step) and it shows that.
 *
 * Everything the member wrote and everything Sharon drafted is rendered by
 * interpolation, as text. Never as HTML.
 */
@Component({
  selector: 'app-conversation-view',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    MatButtonModule,
    MatProgressSpinnerModule,
    PersonLinkComponent,
    ProposalCardComponent,
  ],
  templateUrl: './conversation-view.component.html',
  styleUrls: ['./conversation-view.component.scss'],
})
export class ConversationViewComponent implements OnChanges {
  @Input() conversationId: number | null = null;
  /** A view the parent already holds. When set, no fetch is made. */
  @Input() view: ConversationViewDto | null = null;
  @Output() viewChange = new EventEmitter<ConversationViewDto>();

  readonly modeLabels = MODE_LABELS;
  readonly stateLabels = STATE_LABELS;

  loading = false;
  error: string | null = null;
  notice: string | null = null;
  /** The action in flight, so no second one stacks on it. */
  busy: string | null = null;

  /* The pending draft's editor. */
  draftText = '';
  editReason = '';
  private editingDraftId: number | null = null;
  rejectOpen = false;
  rejectReason = '';
  redraft = false;
  problems: string[] = [];

  /** What "Draft now" answered, when it was not a draft. */
  lastOutcome: TurnOutcomeDto | null = null;
  /** Proposals learned during this visit, kept after they are decided. */
  sessionLearned: ProposalDto[] = [];
  /** Bot turns whose original draft is unfolded. */
  openEdits = new Set<number>();

  thread: ThreadItem[] = [];

  constructor(
    private outreach: OutreachService,
    private dialog: MatDialog,
    private profileDialog: ProfileDialogService
  ) {}

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['view'] && this.view) {
      this.apply(this.view, false);
    } else if (changes['conversationId'] && this.conversationId && !this.view) {
      this.load();
    }
  }

  load(): void {
    if (!this.conversationId) return;
    this.loading = true;
    this.error = null;
    this.outreach.conversation(this.conversationId).subscribe({
      next: (v) => {
        this.loading = false;
        this.apply(v);
      },
      error: (err) => {
        this.loading = false;
        this.error =
          err?.status === 404
            ? `No conversation #${this.conversationId}. It may have been a simulated one that was deleted.`
            : outreachError(err, 'Could not load the conversation');
      },
    });
  }

  private apply(v: ConversationViewDto, emit = true): void {
    this.view = v;
    this.thread = buildThread(v);
    const pending = this.pendingDraft;
    if (pending?.id !== this.editingDraftId) {
      this.editingDraftId = pending?.id ?? null;
      this.draftText = pending?.content ?? '';
      this.editReason = '';
      this.rejectOpen = false;
      this.rejectReason = '';
      this.redraft = false;
      this.problems = [];
    }
    if (emit) this.viewChange.emit(v);
  }

  /* ------------------------------ derived ------------------------------ */

  get isOpenMode(): boolean {
    const m = this.view?.conversation.mode;
    return m === 'assisted' || m === 'auto';
  }

  get pendingDraft(): OutreachDraftDto | null {
    return this.view?.drafts.find((d) => d.status === 'pending') ?? null;
  }

  /** Approved or edited, not sent yet: waiting for their time. */
  get scheduledDrafts(): OutreachDraftDto[] {
    return (this.view?.drafts ?? []).filter(
      (d) => (d.status === 'approved' || d.status === 'edited') && !d.sentTurnId
    );
  }

  get edited(): boolean {
    const d = this.pendingDraft;
    return Boolean(d) && this.draftText.trim() !== d!.content.trim();
  }

  get canDraftNow(): boolean {
    return this.isOpenMode && !this.pendingDraft && this.view?.conversation.state !== 'sending';
  }

  /** The newest pause, for the reason and who or what paused it. */
  get lastPause(): ModeChangeDto | null {
    const changes = (this.view?.modeChanges ?? []).filter((m) => m.toMode === 'paused');
    return changes.length ? changes.reduce((a, b) => (a.changedAt > b.changedAt ? a : b)) : null;
  }

  freezeLabel(reason: string | null): string {
    return reason ? FREEZE_LABELS[reason] ?? reason : 'No reason recorded';
  }

  memberLabel(): string {
    return this.view?.member?.name || 'Member';
  }

  /** Learned-note proposals for one draft: pending ones, and any decided here. */
  learnedFor(draftId: number | null): ProposalDto[] {
    if (!draftId) return [];
    const byId = new Map<number, ProposalDto>();
    for (const p of this.view?.learned ?? []) if (p.status === 'pending') byId.set(p.id, p);
    for (const p of this.sessionLearned) byId.set(p.id, p);
    return [...byId.values()].filter((p) => Number(p.payload?.['draftId']) === draftId);
  }

  onLearnedChanged(p: ProposalDto): void {
    this.sessionLearned = [...this.sessionLearned.filter((x) => x.id !== p.id), p];
  }

  toggleEdit(turnId: number): void {
    if (this.openEdits.has(turnId)) this.openEdits.delete(turnId);
    else this.openEdits.add(turnId);
  }

  isBlocked(d: OutreachDraftDto): boolean {
    return Boolean(d.decidedBy?.startsWith('rule:'));
  }

  personaLines(): { key: string; value: string }[] {
    const p = this.view?.persona;
    if (!p) return [];
    return Object.entries(p)
      .filter(([, v]) => v !== null && v !== undefined && v !== '')
      .map(([key, v]) => ({ key, value: Array.isArray(v) ? v.join(', ') : String(v) }));
  }

  /* ------------------------------- modes ------------------------------- */

  /** Every mode change goes through the confirm dialog, with what happens in one sentence. */
  changeMode(to: DriveMode): void {
    const v = this.view;
    if (!v || this.busy) return;
    const c = v.conversation;
    const pending = Boolean(this.pendingDraft);
    const texts: Record<DriveMode, ActionConfirmData> = {
      auto: {
        title: 'Switch to Auto?',
        description:
          (pending ? 'A pending draft stays pending; ' : '') +
          'Sharon sends the following turns herself, within the freeze rules and the turn budget.' +
          (v.causeTrusted ? '' : ' This cause is not trusted for auto yet.'),
        confirmLabel: 'Switch to Auto',
      },
      assisted: {
        title: 'Switch to Assisted?',
        description:
          'Sharon drafts every following turn for you to send, edit or reject; a turn already due becomes a draft instead of a send.',
        confirmLabel: 'Switch to Assisted',
      },
      paused: {
        title: 'Pause this conversation?',
        description:
          'Nobody writes until you resume. Nothing already sent is undone, and a pending draft survives the pause.',
        confirmLabel: 'Pause',
        reason: { label: 'Note (optional)' },
      },
      human: {
        title: 'Take this conversation over?',
        description:
          'Sharon stops writing here for good and any pending draft expires. ' +
          (c.simulated
            ? 'This is a simulated member, so there is no chat to open.'
            : "The member's profile opens with the team composer, so you reply from there."),
        confirmLabel: 'Take over',
        danger: true,
        reason: { label: 'Note (optional)' },
      },
      closed: {
        title: 'Close this conversation?',
        description: 'Sharon stops, pending drafts expire and nothing more is sent. It cannot be reopened.',
        confirmLabel: 'Close',
        danger: true,
        reason: { label: 'Why (optional, kept as the close reason)' },
      },
    };
    const data =
      c.mode === 'paused' && (to === 'assisted' || to === 'auto')
        ? {
            title: `Resume in ${MODE_LABELS[to]}?`,
            description:
              to === 'auto'
                ? 'Sharon picks up again and sends the following turns herself. A pending draft stays pending.'
                : 'Sharon picks up again and drafts the following turns for you to decide.',
            confirmLabel: 'Resume',
          }
        : texts[to];
    this.confirm(data).subscribe((res) => {
      if (res?.confirmed) this.sendMode(to, res.reason, false);
    });
  }

  private sendMode(to: DriveMode, note: string | undefined, force: boolean): void {
    const id = this.view!.conversation.id;
    this.busy = 'mode';
    this.error = null;
    this.notice = null;
    this.outreach.setMode(id, to, note, force).subscribe({
      next: (v) => {
        this.busy = null;
        this.apply(v);
        this.notice = `Now ${MODE_LABELS[v.conversation.mode]}.`;
        if (to === 'human') this.openComposer();
      },
      error: (err) => {
        this.busy = null;
        if (err?.status === 409 && err?.error?.needsForce && !force) {
          this.confirm({
            title: 'Switch to Auto anyway?',
            description: `${outreachError(err)} Sharon will write and send on her own in this one conversation.`,
            confirmLabel: 'Switch anyway',
            danger: true,
          }).subscribe((res) => {
            if (res?.confirmed) this.sendMode(to, note, true);
          });
          return;
        }
        this.error = outreachError(err, 'Could not change the mode');
      },
    });
  }

  /** The member's profile, with the team composer open: where a takeover replies. */
  openComposer(): void {
    const v = this.view;
    if (!v || v.conversation.simulated) return;
    this.profileDialog
      .open(v.conversation.userId)
      .then((ref) => ref?.componentInstance?.openComposer())
      .catch((err: Error) => (this.error = err.message));
  }

  /* ------------------------------- drafts ------------------------------ */

  send(when: 'now' | 'proposed'): void {
    const d = this.pendingDraft;
    const text = this.draftText.trim();
    if (!d || !text) return;
    const body: DecideDraftBody = this.edited
      ? { kind: 'edit', text, when, ...(this.editReason.trim() ? { reason: this.editReason.trim() } : {}) }
      : { kind: 'approve', when };
    this.decide(d, body);
  }

  reject(): void {
    const d = this.pendingDraft;
    if (!d) return;
    const reason = this.rejectReason.trim();
    this.decide(d, { kind: 'reject', redraft: this.redraft, ...(reason ? { reason } : {}) });
  }

  private decide(d: OutreachDraftDto, body: DecideDraftBody): void {
    if (this.busy) return;
    this.busy = 'draft';
    this.error = null;
    this.notice = null;
    this.problems = [];
    this.outreach.decideDraft(d.id, body).subscribe({
      next: (res) => {
        this.busy = null;
        const { draft, sent, learn, ...view } = res;
        this.apply(view);
        this.notice =
          body.kind === 'reject'
            ? body.redraft
              ? 'Rejected. Sharon is drafting again.'
              : 'Rejected.'
            : sent
              ? 'Sent.'
              : `Scheduled for ${new Date(draft.sendAt ?? draft.proposedSendAt).toLocaleString()}.`;
        if (learn) this.learn(draft.id);
      },
      error: (err) => {
        this.busy = null;
        if (err?.status === 422 && Array.isArray(err?.error?.problems)) this.problems = err.error.problems;
        this.error = outreachError(err, 'Could not decide the draft');
      },
    });
  }

  /** Ask the style learner what this edit or rejection teaches. */
  private learn(draftId: number): void {
    this.outreach.learnFromDraft(draftId).subscribe({
      next: (res) => {
        if (res.proposal) this.onLearnedChanged(res.proposal);
        else if (res.info?.['duplicateOf'])
          this.notice = `${this.notice ?? ''} Style note #${res.info['duplicateOf']} already says this.`.trim();
      },
      error: () => undefined,
    });
  }

  draftNow(): void {
    const v = this.view;
    if (!v || this.busy) return;
    this.busy = 'draftNow';
    this.error = null;
    this.notice = null;
    this.outreach.draftNow(v.conversation.id).subscribe({
      next: ({ outcome, ...view }) => {
        this.busy = null;
        this.lastOutcome = outcome.draft ? null : outcome;
        this.apply(view);
      },
      error: (err) => {
        this.busy = null;
        this.error = outreachError(err, 'Sharon could not draft');
      },
    });
  }

  private confirm(data: ActionConfirmData): Observable<ActionConfirmResult | undefined> {
    return this.dialog
      .open<ActionConfirmDialogComponent, ActionConfirmData, ActionConfirmResult>(ActionConfirmDialogComponent, {
        width: '520px',
        maxWidth: '96vw',
        data,
      })
      .afterClosed();
  }
}

/**
 * Turns and the drafts that never became turns (rejected, blocked by the
 * checks, expired when the member wrote again), in time order. A sent bot turn
 * carries its draft, for the "edited" tag.
 */
export function buildThread(v: ConversationViewDto): ThreadItem[] {
  const draftsById = new Map(v.drafts.map((d) => [d.id, d]));
  const items: ThreadItem[] = v.turns.map((turn) => {
    const draftId = Number(turn.brainOutput?.['draftId']) || null;
    return { kind: 'turn', at: turn.sentAt, turn, draft: draftId ? draftsById.get(draftId) ?? null : null, draftId };
  });
  for (const d of v.drafts) {
    if (d.status === 'rejected' || d.status === 'expired') {
      items.push({ kind: 'draft', at: d.decidedAt ?? d.createdAt, draft: d, draftId: d.id });
    }
  }
  return items.sort((a, b) => a.at.localeCompare(b.at));
}
